<template>
    <b-card id="task-velocity" class="shadow-sm h-100" :class="isPremiumUser() ? velocityScoreClass : ''">
        <template #header>
            <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    <h4 class="mb-0">
                        <b-icon icon="lightning-charge-fill" class="text-primary me-2" />
                        Task Velocity
                        <PremiumTooltip v-if="!isPremiumUser()" />
                    </h4>
                    <template v-if="isPremiumUser()">
                        <b-icon
                            :icon="velocityHelpVisible ? 'x-lg' : 'question-circle'"
                            style="width:20px;color:gray;cursor:pointer;margin-left:10px;"
                            @click.stop="velocityHelpVisible = !velocityHelpVisible"
                        />
                        <b-icon icon="gear" style="width:20px;color:gray;cursor:pointer;margin-left:10px;" id="iconVelocity"/>
                    </template>
                </div>
                <!-- Show score only for premium users -->
                <div v-if="isPremiumUser()" style="min-width: 180px;">
                    <div style="font-size: 0.65rem; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; margin-bottom: 4px; text-align: right;">
                        30-Day Score
                    </div>
                    <div class="d-flex align-items-center justify-content-end" style="margin-bottom: 2px;">
                        <span style="font-size: 2rem; font-weight: 700; line-height: 1;" :style="{ color: velocityScoreColor }">
                            {{ velocityScoreFormatted }}
                        </span>
                        <b-icon
                            :icon="velocityScoreIcon"
                            :style="{ fontSize: '1.4rem', color: velocityScoreColor, marginLeft: '6px' }"
                        />
                    </div>
                    <div style="font-size: 0.72rem; color: #6b7280; line-height: 1.2; text-align: right;">
                        {{ velocityScoreDescription }}
                    </div>
                </div>
            </div>
        </template>

        <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Velocity chart filters -->
        <b-popover v-if="isPremiumUser()" target="iconVelocity" triggers="hover" placement="left">
            <b-form-group
                id="velocity-metric-group"
                label="Metric"
                label-for="velocity-metric"
                description="">
                <select
                    id="velocity-metric"
                    v-model="selectedVelocityMetric"
                    class="form-control">
                    <option value="created">Tasks Created</option>
                    <option value="outstanding">Outstanding Tasks</option>
                </select>
            </b-form-group>
            <b-form-group
                id="velocity-folder-group"
                label="Folder"
                label-for="velocity-folder"
                description="">
                <select
                    id="velocity-folder"
                    v-model="selectedVelocityFolder"
                    class="form-control">
                    <option value="">All Folders</option>
                    <option v-for="folder in folders" :key="folder._id" :value="folder.name">
                        {{ folder.name }}
                    </option>
                </select>
            </b-form-group>
            <b-form-group
                id="velocity-context-group"
                label="Context"
                label-for="velocity-context"
                description="">
                <select
                    id="velocity-context"
                    v-model="selectedVelocityContext"
                    class="form-control">
                    <option value="">All Contexts</option>
                    <option v-for="context in contexts" :key="context._id" :value="context.name">
                        {{ context.name }}
                    </option>
                </select>
            </b-form-group>
            <b-form-group
                id="velocity-priority-group"
                label="Priority"
                label-for="velocity-priority"
                description="">
                <select
                    id="velocity-priority"
                    v-model="selectedVelocityPriority"
                    class="form-control">
                    <option value="">All Priorities</option>
                    <option value="negative">Negative</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="top">Top</option>
                </select>
            </b-form-group>
            <b-form-group
                id="velocity-status-group"
                label="Status"
                label-for="velocity-status"
                description="">
                <select
                    id="velocity-status"
                    v-model="selectedVelocityStatus"
                    class="form-control">
                    <option value="">All Statuses</option>
                    <option value="none">None</option>
                    <option value="next">Next Action</option>
                    <option value="active">Active</option>
                    <option value="planning">Planning</option>
                    <option value="delegated">Delegated</option>
                    <option value="waiting">Waiting</option>
                    <option value="hold">Hold</option>
                    <option value="postponed">Postponed</option>
                    <option value="someday">Someday</option>
                    <option value="canceled">Canceled</option>
                    <option value="reference">Reference</option>
                </select>
            </b-form-group>
        </b-popover>

        <!-- Premium: Show Chart -->
        <div v-if="isPremiumUser()">
            <div v-show="velocityHelpVisible" class="filter-help-panel mb-3">
                <p class="mb-1">{{ velocityChartDescription }}</p>
                <p class="mb-1">The score (top right) is your 30-day completion-to-creation ratio. Above 1.0× means you're closing loops faster than you open them; below 1.0× means your backlog is growing.</p>
                <p class="mb-0">The dashed grey line is a 7-day rolling average of completions — use it to see trends past daily noise. Use the gear to filter by folder, context, priority, or status.</p>
            </div>
            <div class="chart-container">
                <GChart
                    v-if="chartDataVelocityLoaded"
                    type="AreaChart"
                    :data="chartDataVelocity"
                    :options="chartOptionsVelocity"
                    :height="280"
                />
            </div>
            <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Plain-language meaning below chart -->
            <div class="text-muted small mt-2">{{ velocityMeaningLine }}</div>
        </div>

        <!-- Non-Premium: Show Upgrade Message -->
        <div v-else class="py-3">
            <p class="text-muted mb-2">
                Task Velocity reveals whether you're falling behind or staying ahead of your workload. By tracking
                the ratio of tasks completed versus tasks created over time, you can spot trends early and make
                informed decisions about priorities. When velocity is above 1.0x, you're completing more than you're
                creating—building positive momentum and actively reducing your backlog. Below 1.0x signals that work
                is accumulating faster than you can complete it.
            </p>
            <div class="mt-3" style="text-align: right;">
                <b-button variant="primary" size="sm" @click="showVelocityUpgradeModal">
                    <b-icon icon="star-fill" class="me-2" />
                    Upgrade to View
                </b-button>
            </div>
        </div>

        <!-- Premium Upgrade Modal -->
        <PremiumUpgradeModal
            ref="velocityUpgradeModal"
            featureName="Task Velocity"
            featureDescription="Your rolling 7-day ratio of tasks completed vs. tasks created. Above 1.0x, you're ahead. Below 1.0x, your list is growing faster than you can clear it."
            :featureBenefits="['Daily velocity score (X.XXx format)', 'Created vs. completed chart', 'Filter by folder, context, priority, and status']"
            crossSell="Plus: Templates, Kanban board view"
        />
    </b-card>
</template>

<script>

// 20260606 -- Copilot (Claude Sonnet 4.6) -- Extracted from UserStats.vue to fix Vue 3 compat parser bug
// WHY: b-card with complex slot content (b-popover, v-if/v-else, dynamic :class) fails to compile
// inside a new wrapper element in the parent. A simple component reference compiles cleanly.
import { GChart } from 'vue-google-charts'
import AnalyticsService from '../services/AnalyticsService'
import PremiumTooltip from '@/components/PremiumTooltip.vue'
import PremiumUpgradeModal from '@/components/PremiumUpgradeModal.vue'
import genericOps from '../mixins/genericOps'
import taskOps from '../mixins/taskOps'
import Logger from '@/utils/Logger'

const log = Logger.create('VelocityChart')

export default {
    name: 'VelocityChart',
    components: { GChart, PremiumTooltip, PremiumUpgradeModal },
    mixins: [genericOps, taskOps],
    data() {
        return {
            // 20260206 -- Copilot (Claude Sonnet 4.5) -- Velocity score and chart data
            velocityHelpVisible: false,
            velocityScore: 0,
            chartDataVelocity: [['Date', 'Created', 'Completed', 'Daily Velocity']],
            chartDataVelocityLoaded: false,
            // 20260206 -- Copilot (Claude Sonnet 4.5) -- Velocity chart filters
            selectedVelocityMetric: 'created',
            selectedVelocityFolder: '',
            selectedVelocityContext: '',
            selectedVelocityPriority: '',
            selectedVelocityStatus: '',
            chartOptionsVelocity: {
                backgroundColor: 'transparent',
                fontName: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontSize: 13,
                legend: {
                    position: 'bottom',
                    textStyle: { color: '#1f2937', fontSize: 12 }
                },
                chartArea: {
                    left: 60,
                    top: 20,
                    right: 20,
                    bottom: 60,
                    width: '90%',
                    height: '70%'
                },
                hAxis: {
                    textStyle: { color: '#6b7280', fontSize: 11 },
                    gridlines: { color: '#e5e7eb' },
                    minorGridlines: { color: 'transparent' },
                    format: 'M/d'
                },
                vAxis: {
                    textStyle: { color: '#6b7280', fontSize: 11 },
                    gridlines: { color: '#e5e7eb' },
                    minorGridlines: { color: 'transparent' },
                    format: '0',
                    viewWindow: { min: 0 }
                },
                curveType: 'function',
                lineWidth: 3,
                pointSize: 0,
                // 20260606 -- Copilot (Claude Sonnet 4.6) -- 7-day avg is a reference line; muted grey so it recedes behind data series
                colors: ['#10486A', '#10b981', '#9ca3af', '#f59e0b'],
                areaOpacity: 0.15,
                series: {
                    0: { targetAxisIndex: 0 },  // Created/Outstanding - left axis
                    1: { targetAxisIndex: 0 },  // Completed - left axis
                    2: { targetAxisIndex: 0, lineWidth: 2, areaOpacity: 0, lineDashStyle: [4, 4] },  // 7-Day Avg - left, dashed
                    3: { targetAxisIndex: 1, lineWidth: 2, areaOpacity: 0 }  // Daily Velocity - right axis
                },
                vAxes: {
                    0: {
                        title: 'Tasks',
                        textStyle: { color: '#6b7280', fontSize: 11 },
                        titleTextStyle: { color: '#6b7280', fontSize: 12 },
                        gridlines: { color: '#e5e7eb' },
                        format: '0',
                        viewWindow: { min: 0 }
                    },
                    1: {
                        title: 'Velocity',
                        textStyle: { color: '#f59e0b', fontSize: 11 },
                        titleTextStyle: { color: '#f59e0b', fontSize: 12 },
                        gridlines: { count: 0 },
                        format: '0.0x',
                        viewWindow: { min: 0 }
                    }
                },
                tooltip: { textStyle: { fontSize: 12 } }
            },
        }
    },
    computed: {
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Load contexts and folders from store for velocity filters
        contexts() {
            return this.$store.getters.getContexts || []
        },
        folders() {
            return this.$store.getters.getFoldersForProfile || []
        },
        // 20260206 -- Copilot (Claude Sonnet 4.5) -- Velocity chart dynamic description
        velocityChartDescription() {
            if (this.selectedVelocityMetric === 'created') {
                return 'Daily task creation vs completion - green line above blue shows positive momentum'
            } else {
                return 'Outstanding tasks over time - line trending down means backlog is shrinking'
            }
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Plain-language velocity meaning line
        velocityMeaningLine() {
            if (this.velocityScore <= 0) return ''
            if (this.velocityScore >= 1.0) return 'You\'re completing tasks faster than creating them — your backlog is shrinking.'
            return 'Tasks are accumulating faster than you\'re closing them — your backlog is growing.'
        },
        // 20260206 -- Copilot (Claude Sonnet 4.5) -- Velocity score formatting and styling
        velocityScoreFormatted() {
            return this.velocityScore > 0 ? `${this.velocityScore.toFixed(2)}x` : '--'
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- No-data state is neutral, not red
        velocityScoreColor() {
            if (this.velocityScore <= 0) return '#6b7280'
            if (this.velocityScore >= 1.0) return '#10b981'
            if (this.velocityScore >= 0.8) return '#f59e0b'
            return '#ef4444'
        },
        velocityScoreIcon() {
            if (this.velocityScore <= 0) return 'dash-circle'
            if (this.velocityScore >= 1.0) return 'arrow-up-circle-fill'
            if (this.velocityScore >= 0.8) return 'dash-circle-fill'
            return 'arrow-down-circle-fill'
        },
        velocityScoreDescription() {
            if (this.velocityScore <= 0) return 'No velocity data yet'
            if (this.velocityScore >= 1.2) return 'Excellent momentum - completing more than creating'
            if (this.velocityScore >= 1.0) return 'Good pace - staying on top of tasks'
            if (this.velocityScore >= 0.8) return 'Moderate pace - slight backlog growth'
            return 'Low velocity - backlog increasing'
        },
        velocityScoreClass() {
            if (this.velocityScore <= 0) return ''
            if (this.velocityScore >= 1.0) return 'border-start border-success border-4'
            if (this.velocityScore >= 0.8) return 'border-start border-warning border-4'
            return 'border-start border-danger border-4'
        },
    },
    watch: {
        // 20260206 -- Copilot (Claude Sonnet 4.5) -- Velocity filter watchers
        selectedVelocityMetric() {
            this.getDataForVelocityChart(30)
        },
        selectedVelocityFolder() {
            this.getDataForVelocityChart(30)
        },
        selectedVelocityContext() {
            this.getDataForVelocityChart(30)
        },
        selectedVelocityPriority() {
            this.getDataForVelocityChart(30)
        },
        selectedVelocityStatus() {
            this.getDataForVelocityChart(30)
        }
    },
    async created() {
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Skip data fetch for non-premium; they only see the upgrade CTA
        if (!this.isPremiumUser()) return
        await this.$store.dispatch('getFolders')
        await this.$store.dispatch('getContexts')
        this.getDataForVelocityChart(30)
    },
    methods: {
        // 20260206 -- Copilot (Claude Sonnet 4.5) -- Show velocity upgrade modal for non-premium users
        showVelocityUpgradeModal() {
            this.$refs.velocityUpgradeModal.show()
        },
        // 20260206 -- Copilot (Claude Sonnet 4.5) -- Load velocity data: created vs completed
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Added 7-day rolling average as 4th series
        getDataForVelocityChart(days = 30) {
            this.chartDataVelocityLoaded = false

            const h1 = this.selectedVelocityMetric === 'created' ? 'Created' : 'Outstanding Tasks'
            const h2 = this.selectedVelocityMetric === 'created' ? 'Completed' : 'Tasks Completed'
            this.chartDataVelocity = [['Date', h1, h2, '7-Day Avg', 'Daily Velocity']]

            const that = this
            const userId = this.$store.getters.getUser._id
            const token = this.$store.getters.getUserToken

            const filter = { metric: this.selectedVelocityMetric }
            if (this.selectedVelocityFolder) filter.folder = this.selectedVelocityFolder
            if (this.selectedVelocityContext) filter.context = this.selectedVelocityContext
            if (this.selectedVelocityPriority) filter.priority = this.selectedVelocityPriority
            if (this.selectedVelocityStatus) filter.status = this.selectedVelocityStatus

            AnalyticsService.getDashboardVelocity(userId, days, filter, token).then(function (response) {
                const result = response.data.response

                if (result.successful && result.data) {
                    const dataByDay = result.data.dataByDay || {}
                    const completedByDay = result.data.completedByDay || {}
                    that.velocityScore = result.data.velocityScore || 0

                    const now = new Date()
                    now.setUTCHours(0, 0, 0, 0)

                    // Build raw rows first so rolling average can look back 7 days
                    const rawRows = []
                    for (let i = days - 1; i >= 0; i--) {
                        const date = new Date(now)
                        date.setUTCDate(date.getUTCDate() - i)
                        const timestamp = date.getTime()
                        const primaryValue = dataByDay[timestamp] || 0
                        const completed = completedByDay[timestamp] || 0
                        let dailyVelocity = null
                        if (that.selectedVelocityMetric === 'created') {
                            dailyVelocity = primaryValue > 0 ? (completed / primaryValue) : 0
                        }
                        // Outstanding metric: null tells Google Charts to omit this series point (0 draws a misleading flat line)
                        rawRows.push([new Date(timestamp), primaryValue, completed, dailyVelocity])
                    }

                    // Append 7-day rolling average of completed column — smooths daily noise
                    rawRows.forEach((row, idx) => {
                        const window = rawRows.slice(Math.max(0, idx - 6), idx + 1)
                        const avg = window.reduce((sum, r) => sum + r[2], 0) / window.length
                        that.chartDataVelocity.push([row[0], row[1], row[2], Math.round(avg * 10) / 10, row[3]])
                    })

                    // 20260210 -- Copilot (Claude Sonnet 4.5) -- Use $nextTick to ensure DOM is ready before chart renders
                    that.$nextTick(() => {
                        that.chartDataVelocityLoaded = true
                    })
                } else {
                    log.warn('No velocity data returned')
                    that.$nextTick(() => { that.chartDataVelocityLoaded = true })
                }
            }).catch((error) => {
                log.error('getDataForVelocityChart error', error)
                that.$nextTick(() => {
                    that.chartDataVelocityLoaded = true
                })
            })
        },
    }
}
</script>

<style scoped>
/* Replicate UserStats.vue card overrides — scoped CSS doesn't cross component boundaries */
.card {
    border-radius: 8px;
    border: 1px solid #e0e0e0;
}

.card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    padding: 1rem 1.25rem;
}

.shadow-sm {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

.me-2 {
    margin-right: 0.5rem;
}

.chart-container {
    overflow: hidden;
    width: 100%;
}

/* Matches Tasks.vue filter-help-panel — scoped CSS doesn't cross component boundaries */
.filter-help-panel {
    background-color: #e9ecef;
    border-radius: 0.25rem;
    padding: 0.75rem 1rem;
    font-size: 0.85rem;
    color: #495057;
}

.filter-help-panel ul {
    padding-left: 1.25rem;
    margin-bottom: 0;
}
</style>
